import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  fetchBackroomV22Messages,
  fetchBackroomV22Summary,
  fetchBackroomV22Presence,
  postBackroomV22FromCockpit,
  postBackroomV22PresenceFromCockpit,
  normalizeV22Message,
  createBackroomOperationId,
} from '../backroomV2Api';
import {
  classifyFetchFailure,
  shouldApplyFetchResult,
  shouldSkipIntervalTick,
  isDuplicateCompose,
  snippet,
} from './backroomLoadPolicy.mjs';
import { projectBackroomV2 } from '../lib/backroom-v2.js';
import { presenceDue, markPresencePulse } from '../extensusEntity';

const STAGES = [
  { id:'PROPOSAL', label:'Proposal', hint:'Structured change request' },
  { id:'STATUS', label:'Status', hint:'Coordination progress; execution authority lives in Ownership v2' },
  { id:'HANDOFF', label:'Handoff', hint:'Transfer context and execution evidence' },
  { id:'VERIFY', label:'Verify', hint:'Verification evidence; choose canonical scope explicitly when applicable' },
  { id:'BLOCKER', label:'Blocker', hint:'Report a coordination blocker' },
  { id:'DECIDE', label:'Decide', hint:'Operator-gated decision, canonical-fenced' },
  { id:'ORDERS', label:'Orders', hint:'Operator-gated direction, canonical-fenced' },
];
const VALID_KINDS = new Set(STAGES.map((s)=>s.id));

function stageMeta(id) {
  return STAGES.find((s)=>s.id === id) || STAGES.find((s)=>s.id === 'STATUS');
}

function eventFingerprint(value) {
  return JSON.stringify(value, Object.keys(value).sort());
}

const Backroom = ({ channel = 'jarvis-gemini-transition', userTimeZone }) => {
  const [messages,setMessages] = useState([]);
  const [summary,setSummary] = useState({});
  const [summaryReceipt,setSummaryReceipt] = useState(null);
  const [canonicalObservation,setCanonicalObservation] = useState(null);
  const [presence,setPresence] = useState([]);
  const [newMessage,setNewMessage] = useState('');
  const [composeStage,setComposeStage] = useState('STATUS');
  const [taskId,setTaskId] = useState('');
  const [sender,setSender] = useState('EXTENSUS');
  const [loading,setLoading] = useState(false);
  const [loadError,setLoadError] = useState(null);
  const [sendError,setSendError] = useState(null);
  const [reconnecting,setReconnecting] = useState(false);
  const [lastSyncedAt,setLastSyncedAt] = useState(null);
  const fetchGenerationRef = useRef(0);
  const inFlightRef = useRef(false);
  const lastSyncedAtRef = useRef(null);
  const sendingRef = useRef(false);
  const pendingSendRef = useRef(null);
  const lastPostRef = useRef({body:'',at:0});

  const parseDateSafely = (value) => {
    if (!value) return '—';
    const d = new Date(String(value).replace(' ','T'));
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true,timeZone:userTimeZone});
  };

  const loadMessages = useCallback(async ({fromInterval=false}={}) => {
    if (fromInterval && shouldSkipIntervalTick(inFlightRef.current)) return;
    const generation = ++fetchGenerationRef.current;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const [summaryResult,presenceResult] = await Promise.all([
        fetchBackroomV22Summary(channel),
        fetchBackroomV22Presence(channel),
      ]);
      if (!shouldApplyFetchResult(generation,fetchGenerationRef.current)) return;
      if (!summaryResult.success) {
        const kind = classifyFetchFailure(Boolean(lastSyncedAtRef.current),summaryResult.code);
        setReconnecting(kind === 'reconnecting');
        setLoadError(summaryResult.error || kind);
        return;
      }
      const incomingSummary = summaryResult.summary || {};
      const latestSeq = Number(incomingSummary.latest_seq || summaryResult.summary_receipt?.latest_seq || 0);
      const afterSeq = Math.max(0,latestSeq - 100);
      const result = await fetchBackroomV22Messages(channel,{afterSeq,limit:200});
      if (!shouldApplyFetchResult(generation,fetchGenerationRef.current)) return;
      if (!result.success) {
        const kind = classifyFetchFailure(Boolean(lastSyncedAtRef.current),result.code);
        setReconnecting(kind === 'reconnecting');
        setLoadError(result.error || kind);
        return;
      }
      const normalized = (Array.isArray(result.messages)?result.messages:[]).map(normalizeV22Message);
      setMessages(normalized);
      setSummary(incomingSummary);
      setSummaryReceipt(summaryResult.summary_receipt || null);
      setCanonicalObservation(summaryResult.canonical_observation || null);
      setPresence(Array.isArray(presenceResult?.presence)?presenceResult.presence:[]);
      setLoadError(null);
      setReconnecting(false);
      const now = new Date();
      lastSyncedAtRef.current = now;
      setLastSyncedAt(now);
    } finally {
      if (shouldApplyFetchResult(generation,fetchGenerationRef.current)) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  },[channel]);

  useEffect(()=>{
    fetchGenerationRef.current += 1;
    inFlightRef.current = false;
    lastSyncedAtRef.current = null;
    setMessages([]); setSummary({}); setPresence([]); setLastSyncedAt(null); setLoadError(null);
    loadMessages();
  },[loadMessages]);

  useEffect(()=>{
    const id=setInterval(()=>{
      if (typeof document !== 'undefined' && document.hidden) return;
      loadMessages({fromInterval:true});
    },30000);
    return ()=>clearInterval(id);
  },[loadMessages]);

  useEffect(()=>{
    let cancelled=false;
    const pulse=async()=>{
      if(!presenceDue()) return;
      const result=await postBackroomV22PresenceFromCockpit(channel,{current_task_id:taskId || null,metadata:{source_surface:'jarvis-cockpit'}});
      if(!cancelled && (result?.success || result?.ok)) markPresencePulse();
    };
    pulse().catch(()=>{});
    const id=setInterval(()=>pulse().catch(()=>{}),5*60*1000);
    return ()=>{cancelled=true;clearInterval(id);};
  },[channel,taskId]);

  const room = useMemo(()=>projectBackroomV2({
    messages,
    board:{presence,observed_at:lastSyncedAt?.toISOString?.() || null},
    summary,
    summaryReceipt,
    canonicalObservation,
    now:Date.now(),
  }),[messages,presence,summary,summaryReceipt,canonicalObservation,lastSyncedAt]);

  const sendEnvelope = useCallback(async (message) => {
    const semantic = {...message};
    const fingerprint = eventFingerprint(semantic);
    let pending = pendingSendRef.current;
    if (!pending || pending.fingerprint !== fingerprint) {
      pending = {fingerprint,key:createBackroomOperationId()};
      pendingSendRef.current = pending;
    }
    const result = await postBackroomV22FromCockpit(channel,semantic,{idempotencyKey:pending.key});
    if (result.success || result.replay) pendingSendRef.current = null;
    return result;
  },[channel]);

  const handleAck = async (subjectId,kind) => {
    if (sendingRef.current) return;
    sendingRef.current=true; setSendError(null);
    try {
      const result=await sendEnvelope({kind,reply_to:subjectId,content:kind === 'ACK' ? 'Acknowledged.' : 'Rejected.',requires_ack:false});
      if(!result.success) setSendError(result.error || `${kind} failed`);
      else loadMessages();
    } finally { sendingRef.current=false; }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const cleaned=newMessage.trim().replace(/^(\[[A-Z_]+\]\s*)+/i,'').trim();
    if(!cleaned || sendingRef.current) return;
    const kind=String(composeStage || 'STATUS').trim().toUpperCase();
    if(!VALID_KINDS.has(kind)) { setSendError(`Unsupported Backroom kind: ${kind}`); return; }
    const now=Date.now();
    if(isDuplicateCompose(lastPostRef.current.body,lastPostRef.current.at,cleaned,now)) {
      setSendError('Duplicate blocked — the previous committed post already represents this action.');
      return;
    }
    const canonicalRevision = canonicalObservation?.revision ?? null;
    if((kind === 'DECIDE' || kind === 'ORDERS') && canonicalRevision == null) {
      setSendError('Canonical observation unavailable; operator action not sent.');
      return;
    }
    const message={
      kind,
      content:cleaned,
      ...(taskId.trim()?{task_id:taskId.trim()}:{}),
      requires_ack:false,
      ...(kind === 'DECIDE' || kind === 'ORDERS' ? {canonical_revision_ref:Number(canonicalRevision)} : {}),
      metadata:{source_surface:'jarvis-cockpit',displayed_sender:sender || 'EXTENSUS'},
    };
    sendingRef.current=true; setLoading(true); setSendError(null);
    try {
      const result=await sendEnvelope(message);
      if(result.success) {
        lastPostRef.current={body:cleaned,at:Date.now()};
        setNewMessage('');
        await loadMessages();
      } else setSendError(result.error || 'Post failed');
    } finally { sendingRef.current=false; setLoading(false); }
  };

  const pendingAcks = Array.isArray(summary.pending_acks) ? summary.pending_acks : [];
  const conflicts = Array.isArray(summary.conflicts) ? summary.conflicts : [];

  return (
    <div className="flex flex-col h-full w-full max-w-5xl mx-auto gap-3" data-testid="backroom-first-class">
      <div className="border border-zinc-800 bg-zinc-950/90 rounded-lg p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-black tracking-widest text-white uppercase">Backroom atomic control room</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">Teleporte owns coordination · Ownership v2 owns execution · D1 owns canonical truth · JARVIS projects only.</div>
          </div>
          <button type="button" onClick={()=>loadMessages()} className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-zinc-700 rounded bg-black">Refresh</button>
        </div>
        <div className="text-[10px] font-mono text-zinc-500">
          ledger {summary.through_seq ?? '—'}/{summary.latest_seq ?? '—'} · {summary.projection_state || '—'} · hash {summaryReceipt?.coordination_hash?.slice?.(0,12) || '—'} · D1 rev {canonicalObservation?.revision ?? '—'}
        </div>
      </div>

      {reconnecting && <div className="bg-amber-900/40 border border-amber-600/60 text-amber-200 p-2 rounded text-xs">Reconnecting — retaining the last coherent snapshot.</div>}
      {loadError && !reconnecting && <div className="bg-red-900/40 border border-red-600/60 text-red-200 p-2 rounded text-xs">{loadError}</div>}
      {sendError && <div className="bg-red-900/50 border border-red-500 text-red-200 p-2 rounded text-xs">{sendError}</div>}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-[11px]">
        <div className="border border-cyan-900/50 rounded p-2"><b className="text-cyan-300">LIVE</b><div className="text-zinc-300 mt-1">{room.snippets.live}</div></div>
        <div className="border border-emerald-900/50 rounded p-2"><b className="text-emerald-300">OWNS</b><div className="text-zinc-300 mt-1">{room.snippets.owns}</div></div>
        <div className="border border-indigo-900/50 rounded p-2"><b className="text-indigo-300">AWAITS ACK</b><div className="text-zinc-300 mt-1">{room.snippets.awaits}</div></div>
        <div className="border border-red-900/50 rounded p-2"><b className="text-red-300">BLOCKED</b><div className="text-zinc-300 mt-1">{room.snippets.blocked}</div></div>
      </div>

      {room.packets.length > 0 && (
        <div className="border border-amber-700/50 bg-amber-950/20 rounded p-3 text-[11px]" data-testid="operator-packets">
          <b className="text-amber-300">OPERATOR REQUIRED</b>
          <div className="text-zinc-300 mt-1">{room.packets.map((p)=>`${p.reason} @ ${p.through_seq ?? '—'}/${p.latest_seq ?? '—'}`).join(' · ')}</div>
        </div>
      )}

      {pendingAcks.length > 0 && (
        <div className="border border-indigo-900/50 rounded p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">Open acknowledgement contracts</div>
          {pendingAcks.map((id)=>{
            const subject=messages.find((m)=>m.message_id===id);
            return <div key={id} className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="font-mono text-zinc-500">#{id}</span>
              <span className="text-zinc-300 flex-1">{snippet(subject?.content || 'subject not in current tail',120)}</span>
              <button type="button" onClick={()=>handleAck(id,'ACK')} className="px-2 py-1 border border-emerald-700 rounded text-emerald-300">ACK</button>
              <button type="button" onClick={()=>handleAck(id,'NACK')} className="px-2 py-1 border border-red-700 rounded text-red-300">NACK</button>
            </div>;
          })}
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="border border-red-900/50 rounded p-3 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold">Open conflicts</div>
          {conflicts.map((c)=><div key={c.conflict_id} className="text-[11px] text-zinc-300 font-mono">{c.conflict_id} · rev {c.conflict_revision} · {c.conflict_type}</div>)}
        </div>
      )}

      <div className="flex-1 min-h-[300px] max-h-[48vh] overflow-y-auto border border-zinc-800 rounded-lg bg-black/50 p-3 space-y-2">
        {messages.length===0 ? <p className="text-zinc-500 text-xs italic text-center py-8">No typed events in the current ledger tail.</p> : messages.map((msg)=>{
          const meta=stageMeta(msg.kind);
          return <div key={msg.message_id} className="rounded border border-zinc-800 bg-zinc-950/60 p-2.5 text-[11px]">
            <div className="flex justify-between gap-2 mb-1"><span className="font-bold text-blue-300">{msg.principal || 'invalid-principal'} · {meta.label}</span><span className="text-zinc-500">#{msg.thread_seq} · {parseDateSafely(msg.created_at)}</span></div>
            <p className="text-zinc-200 whitespace-pre-wrap">{msg.content}</p>
          </div>;
        })}
      </div>

      <form onSubmit={handleSend} className="border border-zinc-800 rounded-lg bg-zinc-950/80 p-3 space-y-2">
        <div className="flex flex-wrap gap-1.5">{STAGES.map((s)=><button key={s.id} type="button" onClick={()=>setComposeStage(s.id)} className={`text-[10px] uppercase px-2 py-1 rounded border ${composeStage===s.id?'border-white text-white':'border-zinc-800 text-zinc-500'}`}>{s.label}</button>)}</div>
        <div className="text-[10px] text-zinc-500">{stageMeta(composeStage).hint}</div>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <input value={sender} onChange={(e)=>setSender(e.target.value)} placeholder="Display label" className="bg-black border border-zinc-800 text-white text-xs rounded p-2 w-28" />
          <input value={taskId} onChange={(e)=>setTaskId(e.target.value)} placeholder="task_id (optional for root coordination)" className="bg-black border border-zinc-800 text-white text-xs rounded p-2 min-w-[12rem]" />
          <input value={newMessage} onChange={(e)=>setNewMessage(e.target.value)} placeholder="Structured coordination content" className="flex-1 min-w-[12rem] bg-black border border-zinc-800 text-white text-xs rounded p-2" />
          <button type="submit" disabled={loading} className="bg-white text-black font-bold py-2 px-4 rounded text-xs uppercase disabled:opacity-50">Post</button>
        </div>
      </form>
    </div>
  );
};

export default Backroom;
